import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setTalkgroupFilter, setUnitFilter, setFilterRows, setFilterTimeframe, setStarredFilter,
} from "../features/callPlayer/callPlayerSlice";
import {
  useGetTalkgroupsQuery, useGetGroupsQuery, useGetFavoritesQuery,
  useAddFavoriteMutation, useRemoveFavoriteMutation,
} from "../features/api/apiSlice";
import AdvancedFilter, { newRow } from "../Call/components/AdvancedFilter";

const sameTalkgroups = (a, b) => a.length === b.length && a.every((num) => b.includes(num));

function DemoFilterPanel({ shortName, open, onClose }) {
  const dispatch = useDispatch();
  const { data: talkgroupsData } = useGetTalkgroupsQuery(shortName);
  const { data: groupsData } = useGetGroupsQuery(shortName);
  const { data: favorites } = useGetFavoritesQuery(shortName);
  const [addFavorite] = useAddFavoriteMutation();
  const [removeFavorite] = useRemoveFavoriteMutation();
  const globalRows = useSelector((s) => s.callPlayer.filterRows);
  const globalTimeframe = useSelector((s) => s.callPlayer.filterTimeframe);
  const globalStarred = useSelector((s) => s.callPlayer.filterStarred);

  const [tab, setTab] = useState("talkgroup");
  const [tgSel, setTgSel] = useState([]);
  const [tgQuery, setTgQuery] = useState("");
  const [unit, setUnit] = useState("");
  const [rows, setRows] = useState([]);
  const [timeframe, setTf] = useState({ from: null, to: null });
  const [starred, setStarred] = useState(false);

  useEffect(() => {
    if (open) {
      setTgQuery("");
      setStarred(globalStarred);
      setRows(globalRows && globalRows.length ? globalRows.map((r) => ({ ...r })) : []);
      const tf = globalTimeframe || {};
      setTf({ from: tf.from ? new Date(tf.from) : null, to: tf.to ? new Date(tf.to) : null });
    }
  }, [open, globalRows, globalTimeframe, globalStarred]);

  if (!open) return null;

  const talkgroupList = [];
  if (talkgroupsData) for (const n in talkgroupsData.talkgroups) {
    const t = talkgroupsData.talkgroups[n];
    talkgroupList.push({ key: t.num, value: t.num, text: t.description });
  }
  const groupList = [];
  if (Array.isArray(groupsData)) groupsData.forEach((g) => groupList.push({ key: g._id, value: g._id, text: g.groupName }));

  // Search filters the *visible* rows only; tgSel holds the selection and is
  // never touched by searching, so refining the query keeps prior picks.
  const tgq = tgQuery.trim().toLowerCase();
  const filteredTalkgroups = tgq
    ? talkgroupList.filter((t) => (t.text || "").toLowerCase().includes(tgq) || String(t.value).includes(tgq))
    : talkgroupList;
  const toggleTalkgroup = (num) =>
    setTgSel((prev) => (prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num]));

  // Save-as-favourite for the current talkgroup selection (mirrors FilterModal.js).
  const currentTalkgroupFavorite = favorites && tgSel.length > 0 &&
    favorites.find((f) => f.type === "talkgroup" && sameTalkgroups(f.talkgroups, tgSel));
  const toggleTalkgroupFavorite = () => {
    if (currentTalkgroupFavorite) {
      removeFavorite({ shortName, id: currentTalkgroupFavorite._id });
    } else {
      const label = tgSel
        .map((num) => talkgroupList.find((tg) => tg.value === num))
        .filter(Boolean)
        .map((tg) => tg.text)
        .join(", ");
      addFavorite({ shortName, body: { type: "talkgroup", talkgroups: tgSel, label } });
    }
  };

  const apply = () => {
    if (tab === "talkgroup" && tgSel.length) dispatch(setTalkgroupFilter(tgSel));
    else if (tab === "unit" && unit.trim()) dispatch(setUnitFilter(unit.trim()));
    else if (tab === "advanced") {
      const cleaned = rows.filter((r) => ["starred", "emergency"].includes(r.field) || (Array.isArray(r.values) && r.values.length));
      dispatch(setFilterRows(cleaned));
      dispatch(setFilterTimeframe({ from: timeframe.from ? timeframe.from.getTime() : false, to: timeframe.to ? timeframe.to.getTime() : false }));
    }
    dispatch(setStarredFilter(starred));
    onClose();
  };

  // Portal out to .demo-root. The panel is mounted from inside .demo-chips,
  // which is position:fixed with z-index:45 and so forms its own stacking
  // context — trapping this backdrop *below* the top bar (z-index:50) and the
  // bottom player console (z-index:55) no matter how high its own z-index goes.
  // .demo-root itself is position:absolute with z-index:auto, so it is NOT a
  // stacking context: reparenting here lets z-index:60 win at the root level,
  // while still inheriting the --ink/--signal theme vars defined on .demo-root
  // (which a plain document.body target would lose).
  const portalTarget = (typeof document !== "undefined" &&
    document.querySelector(".demo-root")) || (typeof document !== "undefined" ? document.body : null);
  if (!portalTarget) return null;
  return ReactDOM.createPortal(
    <div className="demo-panel-backdrop" onClick={onClose}>
      <div className="demo-panel" onClick={(e) => e.stopPropagation()}>
        <div className="demo-panel-tabs">
          {["talkgroup", "unit", "advanced"].map((t) => (
            <button key={t} className={tab === t ? "active" : ""} onClick={() => {
              if (t === "advanced" && rows.length === 0) setRows([newRow()]);
              setTab(t);
            }}>{t === "talkgroup" ? "Talkgroups" : t === "unit" ? "Unit / RID" : "Advanced"}</button>
          ))}
        </div>
        <div className="demo-panel-body">
          {tab === "talkgroup" && (
            <>
              <input className="demo-panel-tgsearch" placeholder="Search talkgroups…" value={tgQuery}
                autoFocus onChange={(e) => setTgQuery(e.target.value)} />
              <div className="demo-panel-tglist" role="listbox" aria-multiselectable="true">
                {filteredTalkgroups.length === 0 && <div className="demo-panel-tgempty">No matching talkgroups</div>}
                {filteredTalkgroups.map((t) => {
                  const on = tgSel.includes(t.value);
                  return (
                    <button key={t.key} type="button" role="option" aria-selected={on}
                      className={"demo-panel-tgrow" + (on ? " on" : "")} onClick={() => toggleTalkgroup(t.value)}>
                      <span className="demo-panel-tgcheck" aria-hidden="true">{on ? "☑" : "☐"}</span>
                      <span className="demo-panel-tgtext">{t.text}</span>
                    </button>
                  );
                })}
              </div>
              <div className="demo-panel-tgfoot">
                <span>{tgSel.length} selected</span>
                {tgSel.length > 0 && (
                  <button type="button" className="demo-panel-tgclear" onClick={() => setTgSel([])}>Clear</button>
                )}
                <button className="demo-panel-fav" disabled={tgSel.length === 0} onClick={toggleTalkgroupFavorite}>
                  {currentTalkgroupFavorite ? "★ Remove favourite" : "☆ Save as favourite"}
                </button>
              </div>
            </>
          )}
          {tab === "unit" && (
            <input className="demo-panel-unit" placeholder="RID or unit alias…" value={unit}
              onChange={(e) => setUnit(e.target.value)} />
          )}
          {tab === "advanced" && (
            <div className="demo-panel-adv">
              <AdvancedFilter rows={rows} onChange={setRows} timeframe={timeframe}
                onTimeframeChange={setTf} talkgroupList={talkgroupList} groupList={groupList} />
            </div>
          )}
          <label className="demo-panel-starred">
            <input type="checkbox" checked={starred} onChange={() => setStarred(!starred)} /> Only starred calls
          </label>
        </div>
        <div className="demo-panel-actions">
          <button className="demo-btn" onClick={onClose}>Cancel</button>
          <button className="demo-btn demo-btn-primary" onClick={apply}>Apply filter</button>
        </div>
      </div>
    </div>,
    portalTarget
  );
}
export default DemoFilterPanel;
